import * as React from 'react';
import { useState } from 'react';
import { datadogRum } from '@datadog/browser-rum';

export interface AppState {
  id: string;
  firstName: string;
  lastName: string;
  clientName: string;
  clientId: number;
  isAdmin: boolean;
  email: string;
  supportEmail: string;
  enableServiceDesk: boolean;
  globalAnnouncement: string;
  clientAnnouncement: string;
  clientChanging: boolean;
}
type AppStateUpdate = Partial<AppState>;
export interface AppProviderDispatch {
  state: AppState;
  setState: React.Dispatch<React.SetStateAction<AppStateUpdate>>;
}

const isAdmin = localStorage.isAdmin === 'true';
const enableServiceDesk = localStorage.enableServiceDesk === 'true';

const defaultState: AppState = {
  id: localStorage.id,
  firstName: localStorage.firstName,
  lastName: localStorage.lastName,
  clientName: localStorage.clientName,
  clientId: Number(localStorage.clientId),
  email: localStorage.email,
  supportEmail: localStorage.supportEmail,
  isAdmin,
  enableServiceDesk,
  globalAnnouncement: '',
  clientAnnouncement: '',
  clientChanging: false
};

const AppContext = React.createContext<AppProviderDispatch>(null);

const AppContextConsumer = AppContext.Consumer;

const updateDataDogContext = (state: AppState, newValue: AppStateUpdate) => {
  const ddUserContext = {
    client: state.clientId,
    clientName: state.clientName,
    isAdmin: state.isAdmin,
    type: state.isAdmin ? 'admin' : 'user',
    id: state.id
  };

  // Only update custom datadog context if a property of user state tracked in datadog has changed
  const userkeys = ['clientId', 'clientName', 'isAdmin', 'id'];
  if (Object.keys(newValue).filter((key) => userkeys.includes(key) && newValue[key] != state[key]).length > 0) {
    Object.keys(newValue)
      .filter((key) => userkeys.includes(key))
      .forEach((key) => {
        switch (key) {
          case 'clientId':
            ddUserContext.client = newValue[key];
            break;
          case 'isAdmin':
            ddUserContext.type = newValue.isAdmin ? 'admin' : 'user';
            ddUserContext.isAdmin = newValue.isAdmin;
            break;
          default:
            ddUserContext[key] = newValue[key];
            break;
        }
      });

    datadogRum.addRumGlobalContext('usr', ddUserContext);
  }
};

/**
 * Developer note: LocalStorage is read to set the default state of the app Context, but updates to Context must be set directly.
 * Be mindful to update both LocalStorage and the state manually when updating values, not just LocalStorage.
 * LocalStorage is used because Context cannot be set outside of a functional component.
 * This can be refactored to only use Context and bypass LocalStorage, but required significant regression testing due to the number of files affected.
 */
const AppContextProvider = (props: { initialState?: AppState; children: React.ReactNode }) => {
  const [state, setAppState] = useState<AppState>(props.initialState || defaultState);

  React.useEffect(() => {
    datadogRum.addRumGlobalContext('usr', {
      client: defaultState.clientId,
      clientName: defaultState.clientName,
      isAdmin: defaultState.isAdmin,
      type: defaultState.isAdmin ? 'admin' : 'user',
      id: defaultState.id
    });
  }, []);

  const setState = (newValue: AppStateUpdate) => {
    updateDataDogContext(state, newValue);

    setAppState((prevState) => {
      return { ...prevState, ...newValue };
    });
  };

  return <AppContext.Provider value={{ state, setState }}>{props.children}</AppContext.Provider>;
};

export { AppContext, AppContextProvider, AppContextConsumer };
